import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { UserRoles } from '../model/UserRoles';
import { UserDetails } from '../model/UserDetails';

@Injectable({
  providedIn: 'root'
})
export class UserRolesService {

  //Match to roles here: https://confluence:8443/spaces/GMTPRD/pages/445814124/User+Roles
  DD_ROLES_TAB_ACCESS = [
    'MFDST_MFDSTDN_DIGITIZER_RW_ROLE',  //External user + role = Lender
    'MFDST_MFDSTDN_DIGITIZER_PROD_RW_ROLE',
    'SG-GA6-MFDIGITIZER-UAT-ReadWrite',  //Digitizer Admin
    'SG-GA6-MFDIGITIZER-PROD-ReadWrite',  //Digitizer Admin
    'SG-GA6-MFDIGITIZER-UAT-MFASSETMGR-ROLE',  //MF Asset Manager
    'SG-GA6-MFDIGITIZER-PROD-MFASSETMGR-ROLE', //MF Asset Manager
    'SG-GA6-MFDIGITIZER-MFOps-UAT-ROLE',  //MF Operations
    'SG-GA6-MFDIGITIZER-MFOps-PROD-ROLE',  //MF Operations
    'SG-GA6-MFDIGITIZER-UAT-MA-ROLE',  //M&A
    'SG-GA6-MFDIGITIZER-PROD-MA-ROLE',  //M&A
    'SG-GA6-MFDIGITIZER-UAT-ReadOnly',  //Read Only
    'SG-GA6-MFDIGITIZER-PROD-ReadOnly',  //Read Only
  ];

  INSURANCE_ROLES_TAB_ACCESS = [
    'MFDST_MFDSTDN_DIGITIZER_RW_ROLE',  //External user + role = Lender
    'MFDST_MFDSTDN_DIGITIZER_PROD_RW_ROLE',
    'SG-GA6-MFDIGITIZER-UAT-ReadWrite',  //Digitizer Admin
    'SG-GA6-MFDIGITIZER-PROD-ReadWrite',  //Digitizer Admin
    'SG-GA6-MFDIGITIZER-UAT-ReadOnly',  //Read Only
    'SG-GA6-MFDIGITIZER-PROD-ReadOnly',  //Read Only
    'SG-GA6-MFDIGITIZER-UAT-MFINSURANCE-ROLE',  //MF Insurance Role
    'SG-GA6-MFDIGITIZER-PROD-MFINSURANCE-ROLE',  //MF Insurance Role
    'SG-GA6-MFDIGITIZER-MFOps-UAT-ROLE',  //MF Operations
    'SG-GA6-MFDIGITIZER-MFOps-PROD-ROLE',  //MF Operations
  ];

  MFOpsUser_ROLES = [
    'SG-GA6-MFDIGITIZER-MFOps-UAT-ROLE',  //MF Operations
    'SG-GA6-MFDIGITIZER-MFOps-PROD-ROLE'  //MF Operations
  ];

  INSURANCE_ROLES = [
    'SG-GA6-MFDIGITIZER-UAT-MFINSURANCE-ROLE',  //MF Insurance Role
    'SG-GA6-MFDIGITIZER-PROD-MFINSURANCE-ROLE',  //MF Insurance Role
  ];

  ASSET_MANAGER_ROLES = [
    'SG-GA6-MFDIGITIZER-UAT-MFASSETMGR-ROLE',  //MF Asset Manager
    'SG-GA6-MFDIGITIZER-PROD-MFASSETMGR-ROLE'  //MF Asset Manager
  ];

  MA_ROLES = [
    'SG-GA6-MFDIGITIZER-UAT-MA-ROLE',  //M&A
    'SG-GA6-MFDIGITIZER-PROD-MA-ROLE'  //M&A
  ];

  DIGITIZER_ADMIN_ROLES = [
    'SG-GA6-MFDIGITIZER-UAT-ReadWrite',  //Digitizer Admin
    'SG-GA6-MFDIGITIZER-PROD-ReadWrite'  //Digitizer Admin
  ];

  private userRolesSubject = new BehaviorSubject<UserRoles>(null);
  userRoles$ = this.userRolesSubject.asObservable();

  constructor() { }

  updateUserDetails(data: UserRoles) {
    this.userRolesSubject.next(data)
  }

  /**
 * Checks if the given UserDetails is a lender.
 * @param UserDetails - The UserDetails to check.
 * @returns `true` if the role is a lender, otherwise `false`.
 */
  private isLender(userDetails: UserDetails): boolean {
    let isExternalUser = userDetails.isExternalUser;
    let roles: any[] = userDetails.groups;
    if (roles && roles.length > 0) {
      return isExternalUser && roles.includes('MFDST_MFDSTDN_DIGITIZER_RW_ROLE');
    }
    return false;
  }

  /**
 * Checks if the Due Diligence Dashboard should be shown for the given user.
 * @param userDetails - The UserDetails to check.
 * @returns `true` if the Due Diligence Dashboard should be shown, otherwise `false`.
 */
  public isShowDueDiligenceDashboard(userDetails: UserDetails): boolean {
    const roles: string[] = userDetails.groups;

    if (roles && roles.length > 0) {
      return roles.some(role => this.DD_ROLES_TAB_ACCESS.includes(role)) || this.isLender(userDetails);
    }
    return false;
  }

  /**
   * Checks if the Insurance Dashboard should be shown for the given user.
   * @param userDetails - The UserDetails to check.
   * @returns `true` if the Insurance Dashboard should be shown, otherwise `false`.
   */
  public isShowInsuranceDashboard(userDetails: UserDetails): boolean {
    const roles: string[] = userDetails.groups;

    if (roles && roles.length > 0) {
      return roles.some(role => this.INSURANCE_ROLES_TAB_ACCESS.includes(role)) || this.isLender(userDetails);
    }

    // If no roles exist or no criteria are met, return false
    return false;
  }



  

  public isMFOpsUser(roles: string[]): boolean {
    if (roles && roles.length > 0) {
      return roles.some(role => this.MFOpsUser_ROLES.includes(role));
    }
    return false;
  }

  public isInsuranceUser(roles: string[]): boolean {
    if (roles && roles.length > 0) {
      return roles.some(role => this.INSURANCE_ROLES.includes(role));
    }
    return false;
  }

  public isAssetManager(roles: string[]): boolean {
    if (roles && roles.length > 0) {
      return roles.some(role => this.ASSET_MANAGER_ROLES.includes(role));
    }
    return false;
  }

  public isMAUser(roles: string[]): boolean {
    if (roles && roles.length > 0) {
      return roles.some(role => this.MA_ROLES.includes(role));
    }
    return false;
  }

  public isDigitizerAdmin(roles: string[]): boolean {
    if (roles && roles.length > 0) {
      return roles.some(role => this.DIGITIZER_ADMIN_ROLES.includes(role));
    }
    return false;
  }
}
